import { FeatureFlagged } from '@backstage/core-app-api';
import {
  Link,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { Settings as SidebarSettings, UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { AI_GATEWAY_KEYS_FEATURE_FLAG } from '@internal/backstage-plugin-ai-gateway-keys';
import { makeStyles } from '@material-ui/core';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import CloudIcon from '@material-ui/icons/CloudCircle';
import ExtensionIcon from '@material-ui/icons/Extension';
import HelpIcon from '@material-ui/icons/Help';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MenuIcon from '@material-ui/icons/Menu';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import { PropsWithChildren, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPlausibleEvent } from '../../PlausibleProvider';
import { HelpPage } from '../help/HelpPage';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const location = useLocation();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const handleHelpClick = () => {
    // Close any other open dialogs before opening help
    // In this simple implementation, we just open the help dialog
    // In a more complex app, you might have a global dialog state manager
    setHelpDialogOpen(true);
    trackPlausibleEvent('Help', {
      SideBarNavigation: `Help clicked`,
    });
  };

  const handleHelpClose = () => {
    setHelpDialogOpen(false);
  };

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Modals">
          <SidebarSearchModal />
          <SidebarItem onClick={handleHelpClick} icon={HelpIcon} text="Help" />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem
            onClick={() =>
              trackPlausibleEvent('Home', {
                SideBarNavigation: `Home clicked`,
              })
            }
            icon={HomeIcon}
            to="catalog"
            text="Home"
          />
          <SidebarItem
            onClick={() =>
              trackPlausibleEvent('Home', {
                SideBarNavigation: `APIs clicked`,
              })
            }
            icon={ExtensionIcon}
            to="api-docs"
            text="APIs"
          />
          <SidebarItem
            onClick={() =>
              trackPlausibleEvent('Home', {
                SideBarNavigation: `Docs clicked`,
              })
            }
            icon={LibraryBooks}
            to="docs"
            text="Docs"
          />
          <SidebarItem
            onClick={() =>
              trackPlausibleEvent('Home', {
                SideBarNavigation: `Create clicked`,
              })
            }
            icon={CreateComponentIcon}
            to="create"
            text="Create..."
          />
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            <SidebarItem
              onClick={() =>
                trackPlausibleEvent('Home', {
                  SideBarNavigation: `DeployNextApp clicked`,
                })
              }
              icon={PlayArrowIcon}
              to="deploy-next-app"
              text="DeployNextApp"
            />
            <SidebarItem
              onClick={() =>
                trackPlausibleEvent('Home', {
                  SideBarNavigation: `Onboarding clicked`,
                })
              }
              icon={CloudIcon}
              to="ccoe-cloud-onboarding"
              text="Onboarding"
            />
            <FeatureFlagged with={AI_GATEWAY_KEYS_FEATURE_FLAG}>
              <SidebarItem
                onClick={() =>
                  trackPlausibleEvent('Home', {
                    SideBarNavigation: `AiGatewayKeys clicked`,
                  })
                }
                icon={VpnKeyIcon}
                to="ai-gateway-keys"
                text="AI Gateway keys"
              />
            </FeatureFlagged>
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
      <HelpPage open={helpDialogOpen} onClose={handleHelpClose} currentLocation={location.pathname} />
    </SidebarPage>
  );
};
